import { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { SERVICES, THERAPIST } from "@/data/catalog";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Check, Loader2, AlertTriangle, ShieldAlert, Mail, FileText, ShieldCheck, Users } from "lucide-react";
import { useT } from "@/i18n/I18nProvider";
import { encryptForAdmin } from "@/lib/e2eCrypto";

export default function BookingSection() {
    const { t } = useT();
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    // Public key of the admin (fetched from /api/e2e/pubkey). If set, sensitive
    // fields (phone + notes) are encrypted client-side before submission.
    const [adminPubKey, setAdminPubKey] = useState(null);
    const [form, setForm] = useState({
        full_name: "", email: "", phone: "", service: "",
        preferred_times: "", notes: "",
        health_consent: false,
        medical_note_consent: false,
        notifications_opt_in: true,
        website: "",
    });

    // Build bookable services with localized service names
    const bookableServices = useMemo(
        () => SERVICES.flatMap((s) =>
            s.variants.map((v) => ({
                id: v.id,
                label: `${t(`svc.${s.id}.name`)} — ${v.label} (${v.price}$)`,
                price: v.price,
            }))
        ),
        [t]
    );

    // Fetch the admin's E2E public key once (silently — absence = feature off)
    useEffect(() => {
        api.get("/e2e/pubkey")
            .then((res) => setAdminPubKey(res.data?.pubkey_jwk || null))
            .catch(() => setAdminPubKey(null));
    }, []);

    const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.full_name || !form.email || !form.phone || !form.service)
            return toast.error(t("book.err_fields"));
        if (!form.health_consent)
            return toast.error(t("book.err_consent1"));
        if (!form.medical_note_consent)
            return toast.error(t("book.err_consent2"));
        setSubmitting(true);
        try {
            // Aggregate preferred availability into notes so Jérémy sees it clearly
            const combinedNotes = [
                form.preferred_times ? `Disponibilités souhaitées : ${form.preferred_times}` : "",
                form.notes,
            ].filter(Boolean).join("\n\n");

            let e2e_payload = null;
            if (adminPubKey) {
                try {
                    e2e_payload = await encryptForAdmin(
                        { phone: form.phone, notes: combinedNotes },
                        adminPubKey,
                    );
                } catch {
                    e2e_payload = null;
                }
            }
            // Backend still expects date + time (regex) — send sentinels
            // "1900-01-01" and "00:00" to indicate "à planifier par Jérémy".
            await api.post("/bookings", {
                full_name: form.full_name,
                email: form.email,
                phone: form.phone,
                service: form.service,
                date: "1900-01-01",
                time: "00:00",
                notes: combinedNotes,
                health_consent: form.health_consent,
                medical_note_consent: form.medical_note_consent,
                notifications_opt_in: form.notifications_opt_in,
                website: form.website,
                ...(e2e_payload ? { e2e_payload } : {}),
            });
            setDone(true);
            toast.success(t("book.toast_success"));
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("book.err_generic"));
        } finally { setSubmitting(false); }
    };

    if (done) {
        const mailHref = `mailto:${THERAPIST.email}?subject=${encodeURIComponent("Suivi — Réservation " + form.full_name)}&body=${encodeURIComponent("Bonjour Jérémy,\n\nJe viens de faire une demande de réservation.\n\nDétails supplémentaires :\n\n")}`;
        const quoteHref = `mailto:${THERAPIST.email}?subject=${encodeURIComponent("Demande de devis — " + (form.full_name || ""))}&body=${encodeURIComponent("Bonjour Jérémy,\n\nJe souhaite obtenir un devis pour :\n\n• Contexte (entreprise, événement, à domicile, autre) : \n• Date envisagée : \n• Nombre de participants : \n• Durée souhaitée : \n\nMerci !")}`;
        return (
            <section id="reservation" data-testid="booking-section" className="py-20 md:py-32 scroll-mt-24">
                <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
                    <div className="w-16 h-16 mx-auto rounded-full bg-[#3D8C95] flex items-center justify-center mb-6">
                        <Check size={32} className="text-white" />
                    </div>
                    <h2 className="font-display text-4xl md:text-5xl text-[#2C2A29] mb-4">{t("book.success_title")}</h2>
                    <p className="text-[#6E6862] leading-relaxed mb-8">
                        {t("book.success_desc", { name: form.full_name })}
                    </p>

                    {/* Actions directes après confirmation */}
                    <div className="flex flex-wrap justify-center gap-3 mb-6" data-testid="success-actions">
                        <a href={mailHref} data-testid="success-mail-order">
                            <Button
                                variant="outline"
                                className="rounded-full border-[#3D8C95] text-[#3D8C95] hover:bg-[#3D8C95] hover:text-white px-6 py-5 text-sm"
                            >
                                <Mail size={14} className="mr-2" />
                                Commander par courriel
                            </Button>
                        </a>
                        <a href={quoteHref} data-testid="success-quote">
                            <Button
                                variant="outline"
                                className="rounded-full border-[#2C2A29] text-[#2C2A29] hover:bg-[#2C2A29] hover:text-white px-6 py-5 text-sm"
                            >
                                <FileText size={14} className="mr-2" />
                                Demander un devis
                            </Button>
                        </a>
                    </div>

                    <Button data-testid="new-booking-btn" onClick={() => {
                        setDone(false);
                        setForm({ full_name: "", email: "", phone: "", service: "", preferred_times: "", notes: "", health_consent: false, medical_note_consent: false, notifications_opt_in: true, website: "" });
                    }} className="rounded-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white px-8 py-6">
                        {t("book.new")}
                    </Button>
                </div>
            </section>
        );
    }

    return (
        <section id="reservation" data-testid="booking-section" className="py-20 md:py-32 scroll-mt-24 bg-[#FAF7F2]">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
                <div className="max-w-2xl mb-14">
                    <div className="text-xs tracking-[0.3em] uppercase text-[#3D8C95] mb-4">{t("book.eyebrow")}</div>
                    <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-light text-[#2C2A29] tracking-tight leading-tight">
                        {t("book.title1")} <span className="italic text-[#3D8C95]">{t("book.title2")}</span>.
                    </h2>
                    <p className="mt-4 text-[#6E6862] leading-relaxed text-base md:text-lg">{t("book.desc")}</p>
                </div>

                <form onSubmit={submit} className="max-w-3xl mx-auto" data-testid="booking-form">
                    {/* Honeypot invisible anti-bot */}
                    <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", overflow: "hidden" }}>
                        <label htmlFor="website">{t("book.honeypot")}</label>
                        <input type="text" id="website" name="website" tabIndex="-1" autoComplete="off"
                            value={form.website} onChange={(e) => upd("website", e.target.value)} />
                    </div>

                    {/* Bannière « places limitées » */}
                    <div data-testid="waitlist-banner" className="mb-6 rounded-2xl border-2 border-[#B7A46B]/40 bg-[#B7A46B]/5 p-4 flex items-start gap-3">
                        <Users size={20} className="text-[#B7A46B] flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-[#2C2A29] leading-relaxed">
                            <b>Places limitées.</b> Jérémy vous contactera sous <b>24 à 48 h</b> pour vous proposer un créneau. Si l&apos;agenda est plein pour la période souhaitée, une <b>liste d&apos;attente</b> est possible.
                        </div>
                    </div>

                    {/* Form fields — carte unique */}
                    <div className="bg-white rounded-3xl border border-[#E8E2D9] p-6 md:p-8 shadow-[0_8px_30px_rgba(44,42,41,0.04)] space-y-5">
                        {adminPubKey && (
                            <div data-testid="e2e-active-banner" className="flex items-center gap-2 px-3 py-2 rounded-xl bg-[#3D8C95]/10 border border-[#3D8C95]/30 text-xs text-[#3D8C95]">
                                <ShieldCheck size={14} />
                                <span>Vos données sensibles sont chiffrées de bout en bout avant l&apos;envoi — seul Jérémy peut les lire.</span>
                            </div>
                        )}
                        <div>
                            <Label htmlFor="full_name" className="text-xs uppercase tracking-[0.2em] text-[#6E6862]">{t("book.name")}</Label>
                            <Input id="full_name" data-testid="input-full-name" value={form.full_name}
                                onChange={(e) => upd("full_name", e.target.value)} placeholder={t("book.name_ph")}
                                className="mt-2 rounded-xl border-[#E8E2D9] focus-visible:ring-[#3D8C95]" required />
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                            <div>
                                <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] text-[#6E6862]">{t("book.email")}</Label>
                                <Input id="email" type="email" data-testid="input-email" value={form.email}
                                    onChange={(e) => upd("email", e.target.value)} placeholder={t("book.email_ph")}
                                    className="mt-2 rounded-xl border-[#E8E2D9] focus-visible:ring-[#3D8C95]" required />
                            </div>
                            <div>
                                <Label htmlFor="phone" className="text-xs uppercase tracking-[0.2em] text-[#6E6862]">{t("book.phone")}</Label>
                                <Input id="phone" data-testid="input-phone" value={form.phone}
                                    onChange={(e) => upd("phone", e.target.value)} placeholder={t("book.phone_ph")}
                                    className="mt-2 rounded-xl border-[#E8E2D9] focus-visible:ring-[#3D8C95]" required />
                            </div>
                        </div>
                        <div>
                            <Label className="text-xs uppercase tracking-[0.2em] text-[#6E6862]">{t("book.service")}</Label>
                            <Select value={form.service} onValueChange={(v) => upd("service", v)}>
                                <SelectTrigger data-testid="select-service" className="mt-2 rounded-xl border-[#E8E2D9] focus:ring-[#3D8C95]">
                                    <SelectValue placeholder={t("book.service_ph")} />
                                </SelectTrigger>
                                <SelectContent>
                                    {bookableServices.map((s) => (
                                        <SelectItem key={s.id} value={s.id} data-testid={`option-${s.id}`}>{s.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label htmlFor="preferred_times" className="text-xs uppercase tracking-[0.2em] text-[#6E6862]">{t("book.availability_label")}</Label>
                            <Textarea id="preferred_times" data-testid="input-availability" value={form.preferred_times}
                                onChange={(e) => upd("preferred_times", e.target.value)}
                                placeholder={t("book.availability_ph")}
                                className="mt-2 rounded-xl border-[#E8E2D9] focus-visible:ring-[#3D8C95] min-h-[70px]" />
                        </div>
                        <div>
                            <Label htmlFor="notes" className="text-xs uppercase tracking-[0.2em] text-[#6E6862]">{t("book.notes")}</Label>
                            <Textarea id="notes" data-testid="input-notes" value={form.notes}
                                onChange={(e) => upd("notes", e.target.value)}
                                placeholder={t("book.notes_ph")}
                                className="mt-2 rounded-xl border-[#E8E2D9] focus-visible:ring-[#3D8C95] min-h-[80px]" />
                        </div>

                        {/* Avertissement 1 — Contre-indications absolues */}
                        <div className="rounded-2xl border-2 border-[#C44569]/40 bg-[#C44569]/5 p-5">
                            <div className="flex items-start gap-3 mb-3">
                                <ShieldAlert size={22} className="text-[#C44569] flex-shrink-0 mt-0.5" />
                                <div>
                                    <h4 className="font-display text-lg text-[#2C2A29] leading-tight">{t("book.warn1_title")}</h4>
                                    <p className="text-xs text-[#6E6862] mt-1">{t("book.warn1_desc")}</p>
                                </div>
                            </div>
                            <ul data-testid="contra-list" className="text-xs text-[#2C2A29] leading-relaxed space-y-1.5 pl-4 mb-4">
                                {[1,2,3,4,5,6,7,8,9,10,11,12].map((n) => (
                                    <li key={n} className="flex items-start gap-2">
                                        <span className="text-[#C44569] font-bold mt-0.5">•</span>
                                        <span>{t(`contra.${n}`)}</span>
                                    </li>
                                ))}
                            </ul>
                            <label className="flex items-start gap-2 cursor-pointer pt-3 border-t border-[#C44569]/20">
                                <Checkbox data-testid="health-consent"
                                    checked={form.health_consent}
                                    onCheckedChange={(v) => upd("health_consent", !!v)}
                                    className="mt-0.5 border-[#C44569] data-[state=checked]:bg-[#C44569] data-[state=checked]:border-[#C44569]" />
                                <span className="text-xs text-[#2C2A29] leading-relaxed font-medium">
                                    {t("book.warn1_check")}
                                </span>
                            </label>
                        </div>

                        {/* Avertissement 2 — Billet médical */}
                        <div className="rounded-2xl border-2 border-[#E07A5F]/40 bg-[#E07A5F]/5 p-5">
                            <div className="flex items-start gap-3 mb-3">
                                <AlertTriangle size={22} className="text-[#E07A5F] flex-shrink-0 mt-0.5" />
                                <div>
                                    <h4 className="font-display text-lg text-[#2C2A29] leading-tight">{t("book.warn2_title")}</h4>
                                </div>
                            </div>
                            <p className="text-xs text-[#2C2A29] leading-relaxed mb-4">
                                {t("book.warn2_body")}
                            </p>
                            <label className="flex items-start gap-2 cursor-pointer pt-3 border-t border-[#E07A5F]/20">
                                <Checkbox data-testid="medical-note-consent"
                                    checked={form.medical_note_consent}
                                    onCheckedChange={(v) => upd("medical_note_consent", !!v)}
                                    className="mt-0.5 border-[#E07A5F] data-[state=checked]:bg-[#E07A5F] data-[state=checked]:border-[#E07A5F]" />
                                <span className="text-xs text-[#2C2A29] leading-relaxed font-medium">
                                    {t("book.warn2_check")}
                                </span>
                            </label>
                        </div>

                        <label className="flex items-start gap-2 cursor-pointer pt-1">
                            <Checkbox data-testid="notif-opt-in"
                                checked={form.notifications_opt_in}
                                onCheckedChange={(v) => upd("notifications_opt_in", !!v)}
                                className="mt-0.5" />
                            <span className="text-xs text-[#6E6862] leading-relaxed">
                                {t("book.notif_optin")}
                            </span>
                        </label>

                        <Button type="submit" data-testid="booking-submit-button" disabled={submitting}
                            className="w-full rounded-full bg-[#E07A5F] hover:bg-[#D06A4F] text-white py-6 text-base transition-transform hover:-translate-y-0.5 shadow-[0_10px_30px_rgba(224,122,95,0.3)]">
                            {submitting ? (<><Loader2 size={18} className="mr-2 animate-spin" />{t("book.sending")}</>) : t("book.submit")}
                        </Button>
                        <p className="text-xs text-[#6E6862] text-center">{t("book.confidential")}</p>
                    </div>
                </form>
            </div>
        </section>
    );
}
